import React, { useState } from 'react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Meter } from '../../components/ui/Meter';
import { Tabs } from '../../components/ui/Tabs';
import { auditLog, moderationQueue, platformMetrics, reportedAccounts, supplyDemand, verificationQueue } from '../../data/admin';
import { institutions } from '../../data/institutions';
import { percent } from '../../utils/format';
const TABS = ['Operations', 'Moderation', 'Verification', 'Platform'] as const;
export function PlatformOverview() {
  const [tab, setTab] = useState<(typeof TABS)[number]>('Operations');
  return <div className="space-y-6" data-id="element-6476" data-mp="gttnrupn06bt">
      <PageHeader eyebrow="Campuzon administration" title="Platform operations" description="Operational administration, moderation, verification and sensitive-data access are deliberately separated. Every action is attributed and logged." data-id="element-6477" data-mp="3paohajkjoed8" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Admin sections" data-id="element-6478" data-mp="34clmuv5myr97" />

      {tab === 'Operations' && <div className="space-y-6" data-id="element-6479" data-mp="392s9wqki23pc">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-6480" data-mp="394mzii6pujzr">
            {platformMetrics.map(metric => <Panel key={metric.label} data-id="element-6481" data-mp="3e1n5yu7h9ore">
                <p className="cz-num text-2xl font-semibold tracking-tight text-ink" data-id="element-6482" data-mp="2gf67vmqx774l">
                  {metric.value}
                </p>
                <p className="mt-1 text-[13px] text-ink-muted" data-id="element-6483" data-mp="2le9dp9eq5hdm">{metric.label}</p>
                <p className="cz-num mt-1 text-[12px] text-ink-subtle" data-id="element-6484" data-mp="2qbsqs5zoro3z">{metric.delta}</p>
              </Panel>)}
          </div>

          <div className="grid gap-6 lg:grid-cols-2" data-id="element-6485" data-mp="2v9ub0u8ulth1">
            <Panel title="Housing supply and demand" description="Aggregated market view — never individual students." bodyClassName="p-0" data-id="element-6486" data-mp="5ljuedd8vfxs">
              <ul className="divide-y divide-line" data-id="element-6487" data-mp="26o1if5h12hnl">
                {supplyDemand.map(row => <li key={row.city} className="flex items-center gap-4 px-5 py-3.5" data-id="element-6488" data-mp="21qja9hau46gn">
                    <p className="w-24 shrink-0 text-[13px] font-medium text-ink" data-id="element-6489" data-mp="2fq107zzv9prl">{row.city}</p>
                    <div className="min-w-0 flex-1" data-id="element-6490" data-mp="2bne5nrbt9qqp">
                      <Meter value={percent(row.supply, row.demand)} label="Supply against demand" trailing={`${percent(row.supply, row.demand)}%`} tone={row.supply / row.demand < 0.6 ? 'warn' : 'brand'} data-id="element-6491" data-mp="2bopazvkwn6jx" />
                    </div>
                    <p className="cz-num w-24 text-right text-[13px] text-ink-muted" data-id="element-6492" data-mp="1rzevoip2mnmc">
                      {row.listings} listings
                    </p>
                  </li>)}
              </ul>
            </Panel>

            <Panel title="Institutions" bodyClassName="p-0" data-id="element-6493" data-mp="1088zuegdvc2v">
              <ul className="divide-y divide-line" data-id="element-6494" data-mp="q4adi9y6lv3a">
                {institutions.map(institution => <li key={institution.id} className="flex items-center gap-3 px-5 py-3.5" data-id="element-6495" data-mp="uvsegy07fvo9">
                    <div className="min-w-0 flex-1" data-id="element-6496" data-mp="g75z4wakv3x1">
                      <p className="text-[13px] font-medium text-ink" data-id="element-6497" data-mp="g4jk6blcxm4s">{institution.name}</p>
                      <p className="text-[12px] text-ink-muted" data-id="element-6498" data-mp="l60hxy8josh5">
                        {institution.place.city}, {institution.place.state} ·{' '}
                        {institution.campuses.length} campus(es)
                      </p>
                    </div>
                    <Badge tone={institution.integrationLevel === 'full' ? 'ok' : institution.integrationLevel === 'directory' ? 'brand' : 'neutral'} data-id="element-6499" data-mp="yw3z4askhvzq">
                      {institution.integrationLevel === 'none' ? 'No integration' : institution.integrationLevel === 'directory' ? 'Directory sync' : 'Full integration'}
                    </Badge>
                  </li>)}
              </ul>
            </Panel>
          </div>
        </div>}

      {tab === 'Moderation' && <div className="grid gap-6 lg:grid-cols-3" data-id="element-6500" data-mp="rnbz2v8wta2h">
          <Panel className="lg:col-span-2" title="Moderation queue" bodyClassName="p-0" data-id="element-6501" data-mp="rkpw78wyriql">
            <ul className="divide-y divide-line" data-id="element-6502" data-mp="mlwef4an95km">
              {moderationQueue.map(item => <li key={item.id} className="px-5 py-4" data-id="element-6503" data-mp="hqz7q40u78aw">
                  <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-6504" data-mp="corsn5kpnc4c">
                    <div className="min-w-0" data-id="element-6505" data-mp="32fdkclnqtuel">
                      <p className="text-[13px] font-semibold text-ink" data-id="element-6506" data-mp="32get2jhae4t5">{item.subject}</p>
                      <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-6507" data-mp="2xepif4xz7t5l">{item.reason}</p>
                      <p className="cz-num mt-0.5 text-[12px] text-ink-subtle" data-id="element-6508" data-mp="1czb13brg9bxm">
                        {item.surface} · {item.reports} report(s) · {item.raised}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2" data-id="element-6509" data-mp="1muur5hjc1geg">
                      <Badge tone={item.severity === 'High' ? 'danger' : item.severity === 'Medium' ? 'warn' : 'neutral'} data-id="element-6510" data-mp="1rnxm9u3rcxwr">
                        {item.severity}
                      </Badge>
                      <Button size="sm" variant="secondary" data-id="element-6511" data-mp="2akfeecmiqztj">
                        Review
                      </Button>
                    </div>
                  </div>
                </li>)}
            </ul>
          </Panel>
          <Panel title="Reported accounts" bodyClassName="p-0" data-id="element-6512" data-mp="g9k2b0c4l2m0">
            <ul className="divide-y divide-line" data-id="element-6513" data-mp="87jhv0xkll3r">
              {reportedAccounts.map(item => <li key={item.id} className="flex items-center gap-3 px-5 py-3.5" data-id="element-6514" data-mp="470p94nusb0i">
                  <div className="min-w-0 flex-1" data-id="element-6515" data-mp="i68sq9r1u5ai">
                    <p className="text-[13px] font-medium text-ink" data-id="element-6516" data-mp="d646s47dgicw">{item.account}</p>
                    <p className="cz-num text-[12px] text-ink-muted" data-id="element-6517" data-mp="d874zbiohhsp">
                      {item.role} · {item.reports} reports
                    </p>
                  </div>
                  <Badge tone={item.state === 'Restricted' ? 'danger' : 'warn'} data-id="element-6518" data-mp="n3sd7recbeaw">{item.state}</Badge>
                </li>)}
            </ul>
          </Panel>
        </div>}

      {tab === 'Verification' && <Panel title="Verification queue" description="Properties, owners, employers and institutions awaiting checks. Approval states exactly what was verified — never that a property was inspected." bodyClassName="p-0" data-id="element-6519" data-mp="vyyecd2o1873">
          <ul className="divide-y divide-line" data-id="element-6520" data-mp="1phwijvzkuqxa">
            {verificationQueue.map(task => <li key={task.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-6521" data-mp="1tgvnhglb6noq">
                <div className="min-w-0 flex-1" data-id="element-6522" data-mp="1fhcgws85bkej">
                  <p className="text-[13px] font-semibold text-ink" data-id="element-6523" data-mp="1khi9qbq5jcy7">{task.subject}</p>
                  <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-6524" data-mp="1kjl8pl9a8gf2">
                    {task.type} · submitted by {task.submittedBy} on {task.submitted}
                  </p>
                </div>
                <Badge tone={task.status === 'Approved' ? 'ok' : task.status === 'Escalated' ? 'danger' : 'warn'} data-id="element-6525" data-mp="1al5idki2xb6m">
                  {task.status}
                </Badge>
                {task.status !== 'Approved' && <Button size="sm" variant="secondary" data-id="element-6526" data-mp="ruxidb9of0un">
                    Open
                  </Button>}
              </li>)}
          </ul>
        </Panel>}

      {tab === 'Platform' && <Panel title="Audit log" description="Attributed record of administrative, moderation, verification and sensitive-data activity." bodyClassName="p-0" data-id="element-6527" data-mp="y9wbbddowbuh">
          <DataTable caption="Attributed administrative audit log" rowKey={entry => entry.id} rows={auditLog} columns={[{
        header: 'Actor',
        primary: true,
        cell: entry => entry.actor
      }, {
        header: 'Role',
        cell: entry => entry.actorRole
      }, {
        header: 'Action',
        cell: entry => entry.action
      }, {
        header: 'Target',
        cell: entry => entry.target
      }, {
        header: 'Scope',
        status: true,
        cell: entry => <Badge tone={entry.scope === 'Sensitive data' ? 'danger' : 'neutral'} data-id="element-6529" data-mp="oh1jc35sd7c">
                    {entry.scope}
                  </Badge>
      }, {
        header: 'Time',
        cell: entry => <span className="cz-num" data-id="element-6530" data-mp="gdihpgitq2i">{entry.time}</span>
      }]} data-id="element-6528" data-mp="obqakz3mdbr7" />
        </Panel>}
    </div>;
}